// Preset icons for areas and spaces: a symbol and a brand color, drawn as a glyph on
// a soft tinted tile. Symbols are keyed by SF Symbol name so iOS draws them natively;
// web maps each key to its closest lucide glyph. Keep apps/ios IconPalette in sync.

export type IconColorName =
  | "ink" | "slate" | "crimson" | "ember" | "ochre" | "lime" | "moss" | "teal" | "blue" | "indigo" | "plum" | "rose";

export interface IconTone {
  /** Glyph color. Darkened (light) or lightened (dark) from the base until it keeps 3.2:1 on its tile. */
  fg: string;
  /** Tile fill, base color with alpha (#RRGGBBAA). */
  tile: string;
}

export interface IconColor {
  name: IconColorName;
  label: string;
  base: string;
  light: IconTone;
  dark: IconTone;
}

/** Aevix neutrals plus the product colors (Stride, HumVision, Avisero, Vix) and harmonizing hues. */
export const ICON_COLORS: IconColor[] = [
  { name: "ink", label: "Ink", base: "#252422", light: { fg: "#252422", tile: "#2524221A" }, dark: { fg: "#F2EFEA", tile: "#F2EFEA1F" } },
  { name: "slate", label: "Slate", base: "#70757D", light: { fg: "#70757D", tile: "#70757D24" }, dark: { fg: "#8A8D93", tile: "#70757D3D" } },
  { name: "crimson", label: "Crimson (Vix)", base: "#CE2029", light: { fg: "#CE2029", tile: "#CE202924" }, dark: { fg: "#D95E63", tile: "#CE20293D" } },
  { name: "ember", label: "Ember (HumVision)", base: "#E96B1A", light: { fg: "#BD5B1C", tile: "#E96B1A24" }, dark: { fg: "#E96E1F", tile: "#E96B1A3D" } },
  { name: "ochre", label: "Ochre", base: "#C8902A", light: { fg: "#9B7228", tile: "#C8902A24" }, dark: { fg: "#C8902A", tile: "#C8902A3D" } },
  { name: "lime", label: "Lime (Avisero)", base: "#B7D91A", light: { fg: "#72831E", tile: "#B7D91A24" }, dark: { fg: "#B7D91A", tile: "#B7D91A3D" } },
  { name: "moss", label: "Moss", base: "#5E8C4A", light: { fg: "#588246", tile: "#5E8C4A24" }, dark: { fg: "#749B62", tile: "#5E8C4A3D" } },
  { name: "teal", label: "Teal", base: "#2A8C82", light: { fg: "#2A847B", tile: "#2A8C8224" }, dark: { fg: "#489B92", tile: "#2A8C823D" } },
  { name: "blue", label: "Blue (Stride)", base: "#2870ED", light: { fg: "#2870ED", tile: "#2870ED24" }, dark: { fg: "#5089EC", tile: "#2870ED3D" } },
  { name: "indigo", label: "Indigo", base: "#5865D6", light: { fg: "#5865D6", tile: "#5865D624" }, dark: { fg: "#7B84DB", tile: "#5865D63D" } },
  { name: "plum", label: "Plum", base: "#8A4F9E", light: { fg: "#8A4F9E", tile: "#8A4F9E24" }, dark: { fg: "#A477B1", tile: "#8A4F9E3D" } },
  { name: "rose", label: "Rose", base: "#C2477A", light: { fg: "#C2477A", tile: "#C2477A24" }, dark: { fg: "#CD6D93", tile: "#C2477A3D" } },
];

export const ICON_COLOR_NAMES = ICON_COLORS.map((c) => c.name) as [IconColorName, ...IconColorName[]];
export const iconColor = (name: string | null | undefined): IconColor =>
  ICON_COLORS.find((c) => c.name === name) ?? ICON_COLORS[0]!;

export interface IconSymbol {
  /** SF Symbol name; also the stored `icon` key. */
  sf: string;
  /** lucide-react export name used on web. */
  lucide: string;
  label: string;
}

/** 48 symbols (6 rows of 8). */
export const ICON_SYMBOLS: IconSymbol[] = [
  { sf: "folder", lucide: "Folder", label: "Folder" },
  { sf: "briefcase", lucide: "Briefcase", label: "Briefcase" },
  { sf: "building.2", lucide: "Building2", label: "Building" },
  { sf: "house", lucide: "House", label: "House" },
  { sf: "chevron.left.forwardslash.chevron.right", lucide: "Code", label: "Code" },
  { sf: "terminal", lucide: "Terminal", label: "Terminal" },
  { sf: "cpu", lucide: "Cpu", label: "Chip" },
  { sf: "brain", lucide: "Brain", label: "Brain" },
  { sf: "iphone", lucide: "Smartphone", label: "Phone" },
  { sf: "desktopcomputer", lucide: "Monitor", label: "Desktop" },
  { sf: "globe", lucide: "Globe", label: "Globe" },
  { sf: "shield", lucide: "Shield", label: "Shield" },
  { sf: "lock", lucide: "Lock", label: "Lock" },
  { sf: "eye", lucide: "Eye", label: "Eye" },
  { sf: "chart.bar", lucide: "ChartColumn", label: "Chart" },
  { sf: "dollarsign.circle", lucide: "CircleDollarSign", label: "Money" },
  { sf: "cart", lucide: "ShoppingCart", label: "Cart" },
  { sf: "shippingbox", lucide: "Package", label: "Package" },
  { sf: "truck.box", lucide: "Truck", label: "Truck" },
  { sf: "megaphone", lucide: "Megaphone", label: "Megaphone" },
  { sf: "envelope", lucide: "Mail", label: "Mail" },
  { sf: "bubble.left", lucide: "MessageCircle", label: "Message" },
  { sf: "person.2", lucide: "Users", label: "People" },
  { sf: "person", lucide: "User", label: "Person" },
  { sf: "figure.walk", lucide: "Footprints", label: "Walk" },
  { sf: "heart", lucide: "Heart", label: "Heart" },
  { sf: "stethoscope", lucide: "Stethoscope", label: "Health" },
  { sf: "dumbbell", lucide: "Dumbbell", label: "Fitness" },
  { sf: "graduationcap", lucide: "GraduationCap", label: "Learning" },
  { sf: "book", lucide: "BookOpen", label: "Book" },
  { sf: "music.note", lucide: "Music", label: "Music" },
  { sf: "camera", lucide: "Camera", label: "Camera" },
  { sf: "paintpalette", lucide: "Palette", label: "Design" },
  { sf: "gamecontroller", lucide: "Gamepad2", label: "Games" },
  { sf: "cup.and.saucer", lucide: "Coffee", label: "Coffee" },
  { sf: "gift", lucide: "Gift", label: "Gift" },
  { sf: "airplane", lucide: "Plane", label: "Travel" },
  { sf: "car", lucide: "Car", label: "Car" },
  { sf: "wrench.and.screwdriver", lucide: "Wrench", label: "Tools" },
  { sf: "hammer", lucide: "Hammer", label: "Build" },
  { sf: "lightbulb", lucide: "Lightbulb", label: "Idea" },
  { sf: "leaf", lucide: "Leaf", label: "Leaf" },
  { sf: "flame", lucide: "Flame", label: "Flame" },
  { sf: "star", lucide: "Star", label: "Star" },
  { sf: "bolt", lucide: "Zap", label: "Bolt" },
  { sf: "target", lucide: "Target", label: "Target" },
  { sf: "calendar", lucide: "Calendar", label: "Calendar" },
  { sf: "sparkles", lucide: "Sparkles", label: "Sparkles" },
];

export const iconSymbol = (sf: string | null | undefined): IconSymbol | undefined => ICON_SYMBOLS.find((s) => s.sf === sf);
